import { useEffect } from 'react';
import { useAuthentication } from '../../auth/hooks/useAuthentication';
import { useGrippContext } from '../../grippContext';
import { SubscriptionClientProvider } from './subscriptionClientProvider';
import { SubscribePayload } from 'graphql-ws';

export const useSubscription = async (
  payload: SubscribePayload,
  onNewData: (data: any) => Promise<void>,
  enabled = true
) => {
  const { logger } = useGrippContext();
  const auth = useAuthentication();
  // Callers build the payload inline, so compare it by value: a changed
  // filter (e.g. a workspace switch) must resubscribe, an identical one must not.
  const payloadKey = JSON.stringify(payload);

  useEffect(() => {
    if (!auth || !auth.isAuthenticated || !auth.accessToken || !enabled) {
      return;
    }

    const client = SubscriptionClientProvider.getInstance(auth.accessToken);

    const onNext = async (result: any) => {
      if (result?.errors?.length) {
        logger?.error(
          new Error(
            `GraphQL subscription error: ${result.errors
              .map((e: { message?: string }) => e?.message ?? 'unknown')
              .join(', ')}`
          )
        );
      }
      if (result?.data == null) {
        return;
      }
      try {
        await onNewData(result);
      } catch (err) {
        logger?.error(err);
      }
    };

    // `subscribe` returns its cleanup synchronously; a refused or failed
    // subscription is reported once, through the logger, not rethrown.
    const unsubscribe = client.subscribe(payload, {
      next: (result) => {
        onNext(result).catch((err) => logger?.error(err));
      },
      error: (error) => logger?.error(error),
      complete: () => undefined,
    });

    return unsubscribe;
  }, [auth, enabled, payloadKey]);
};
